import React, { useEffect, useRef, useState } from 'react';
import { Target, Eye, Shield, Zap, TrendingUp } from 'lucide-react';

/* ── Brand tokens ── */
const B = {
  purple: '#360153',
  gold:   '#cd9623',
  pRgb:   '54,1,83',
  gRgb:   '205,150,35',
  pLight: 'rgba(54,1,83,0.12)',
  dark2:  '#0d0020',
};

/* ── Scroll-triggered fade-in ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const FadeIn = ({ children, delay = 0, direction = 'up', className = '' }) => {
  const [ref, inView] = useInView();
  const t = direction === 'up' ? 'translateY(48px)' : direction === 'left' ? 'translateX(-40px)' : 'translateX(40px)';
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : t,
      transition: `opacity 0.75s ease ${delay}ms, transform 0.75s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Pillars data
═══════════════════════════════════════════════════════════ */
const pillars = [
  {
    num: '01', label: 'VISION', icon: Eye,
    title: 'Our Vision',
    img: 'z_11.png',
    body: 'We want Fundviser Capital to be the name families and businesses turn to when it matters most, not just another investment firm, but a long-term partner that helps real people build lasting wealth, steadily and with confidence.',
    bullets: ['A trusted name across generations', 'Investments that stand the test of time', 'Growing with India, sector by sector', 'Global outlook, local understanding'],
    accentCol: '#360153',
  },
  {
    num: '02', label: 'MISSION', icon: Target,
    title: 'Our Mission',
    img: '/sustainable.jpeg',
    body: 'Our job is straightforward — find the right opportunities, put money to work wisely, and keep our investors informed at every step. We do this through careful research, strong relationships, and over 30 years of real-world experience.',
    metrics: [
      { label: 'Smart Allocation',    desc: 'Right asset at the right time' },
      { label: 'Clear Communication', desc: 'No jargon, just honest updates' },
      { label: 'Proven Track Record', desc: '3 decades of consistent decisions' },
      { label: 'Open & Accountable',  desc: 'BSE-listed for full transparency' },
    ],
    accentCol: '#cd9623',
  },
  {
    num: '03', label: 'VALUES', icon: Shield,
    title: 'Our Values',
    img: 'Trust.png',
    body: 'Good investing starts with good character. Three simple principles have shaped every move we make since day one and they are the reason our investors keep coming back.',
    values: [
      { icon: Shield,     name: 'Honesty First',   desc: 'We tell it straight even when it is not what you want to hear' },
      { icon: Zap,        name: 'Think Long-Term', desc: 'We stay focused on what lasts, not what looks good today' },
      { icon: TrendingUp, name: 'Grow Together',   desc: 'When our investors do well, we know we have done our job right' },
    ],
    accentCol: '#0a8060',
  },
];

/* ═══════════════════════════════════════════════════════════
   ThreePillars — expanding panel accordion
═══════════════════════════════════════════════════════════ */
const ThreePillars = () => {
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const isHovering = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isHovering.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewH = window.innerHeight;
      if (rect.top > viewH * 0.65) setActive(0);
      else if (rect.top > viewH * 0.15) setActive(1);
      else setActive(2);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => { isHovering.current = true; }}
      onMouseLeave={() => { isHovering.current = false; }}
      style={{
        display: 'flex', height: '100%', minHeight: 650, borderRadius: 24,
        overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
      }}
    >
      {pillars.map((p, i) => {
        const Icon = p.icon;
        const isActive = active === i;
        return (
          <div
            key={i}
            style={{
              flex: isActive ? 3 : 1,
              transition: 'flex 0.55s cubic-bezier(0.4,0,0.2,1)',
              background: 'linear-gradient(180deg, #0d0020 0%, #160030 100%)',
              position: 'relative',
              overflow: 'hidden',
              cursor: isActive ? 'default' : 'pointer',
              borderRight: i < pillars.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
            onMouseEnter={() => { isHovering.current = true; setActive(i); }}
          >
            {/* Accent glow */}
            {isActive && (
              <div style={{
                position: 'absolute', top: -40, right: -40, width: 200, height: 200,
                borderRadius: '50%', background: p.accentCol, filter: 'blur(90px)',
                opacity: 0.22, pointerEvents: 'none',
              }} />
            )}

            {/* Collapsed label */}
            {!isActive && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
              }}>
                <span style={{
                  color: `rgba(${B.gRgb},0.55)`, fontSize: 10, fontWeight: 900,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                }}>
                  {p.num}
                </span>
                <div style={{ padding: '8px', borderRadius: 10, background: 'rgba(255,255,255,0.06)' }}>
                  <Icon style={{ width: 18, height: 18, color: `rgba(${B.gRgb},0.7)` }} />
                </div>
                <span style={{
                  color: 'rgba(255,255,255,0.30)', fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  writingMode: 'vertical-rl', textOrientation: 'mixed',
                }}>
                  {p.label}
                </span>
              </div>
            )}

            {/* Expanded content */}
            <div style={{
              position: 'absolute', inset: 0, padding: '40px 36px',
              opacity: isActive ? 1 : 0,
              transform: isActive ? 'none' : 'translateX(16px)',
              transition: 'opacity 0.4s 0.1s, transform 0.4s 0.1s',
              overflowY: 'hidden',
              pointerEvents: isActive ? 'auto' : 'none',
            }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                  <span style={{
                    color: `rgba(${B.gRgb},0.65)`, fontSize: 11, fontWeight: 900,
                    letterSpacing: '0.16em', textTransform: 'uppercase',
                  }}>{p.num}</span>
                  <div style={{ flex: 1, height: 1, background: `rgba(${B.gRgb},0.20)` }} />
                  <div style={{
                    padding: '8px', borderRadius: 10,
                    background: `rgba(${p.accentCol === '#cd9623' ? B.gRgb : B.pRgb},0.18)`,
                  }}>
                    <Icon style={{ width: 18, height: 18, color: p.accentCol }} />
                  </div>
                </div>

                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                  fontWeight: 900, color: '#fff', marginBottom: 16, lineHeight: 1.2,
                }}>
                  {p.title}
                </h3>

                <p style={{ color: 'rgba(255,255,255,0.62)', fontSize: 14, lineHeight: 1.75, marginBottom: 24 }}>
                  {p.body}
                </p>

                {/* Vision bullets */}
                {p.bullets && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {p.bullets.map((b, bi) => (
                      <li key={bi} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.gold, flexShrink: 0 }} />
                        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500 }}>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Mission 2×2 grid */}
                {p.metrics && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {p.metrics.map((m, mi) => (
                      <div key={mi} style={{
                        padding: '14px 16px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)',
                      }}>
                        <p style={{ color: '#fff', fontSize: 12, fontWeight: 800, marginBottom: 3 }}>{m.label}</p>
                        <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: 11 }}>{m.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Values list */}
                {p.values && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {p.values.map((v, vi) => {
                      const VI = v.icon;
                      return (
                        <div key={vi} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '14px 16px', borderRadius: 12,
                          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)',
                        }}>
                          <div style={{ padding: 6, borderRadius: 8, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }}>
                            <VI style={{ width: 14, height: 14, color: B.gold }} />
                          </div>
                          <div>
                            <p style={{ color: '#fff', fontSize: 12, fontWeight: 800, marginBottom: 2 }}>{v.name}</p>
                            <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: 11, lineHeight: 1.5 }}>{v.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pillar image */}
                <div style={{
                  marginTop: 22, borderRadius: 14, overflow: 'hidden', height: 155,
                  border: `1px solid rgba(${B.gRgb},0.14)`,
                  boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
                }}>
                  <img
                    src={p.img}
                    alt={p.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MissionVisionSection
═══════════════════════════════════════════════════════════ */
const MissionVisionSection = () => (
  <section className="tf-pillars py-16 sm:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Section header */}
      <FadeIn className="tf-pillars-hd text-center mb-12">
        <span style={{
          display: 'inline-block', padding: '5px 16px', borderRadius: 999,
          fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
          background: B.pLight, color: B.purple, marginBottom: 12,
        }}>
          What We Stand For
        </span>
        <h2
          className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          The Three Pillars
        </h2>
        <div
          className="mx-auto mt-4 h-[2px] w-12 rounded-full"
          style={{ background: `linear-gradient(to right,${B.purple},${B.gold})` }}
        />
      </FadeIn>

      {/* Desktop accordion */}
      <div className="tf-pillars-wrap hidden md:block">
        <ThreePillars />
      </div>

      {/* Mobile fallback cards */}
      <div className="md:hidden flex flex-col gap-5">
        {pillars.map((p, i) => {
          const Icon = p.icon;
          return (
            <div
              key={i}
              className="rounded-2xl overflow-hidden"
              style={{
                background: `linear-gradient(160deg,${B.dark2},#1a0035)`,
                border: `1px solid rgba(${B.gRgb},0.18)`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
              }}
            >
              {/* Image */}
              <div style={{ position: 'relative', height: 190, overflow: 'hidden' }}>
                <img src={p.img} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${B.dark2} 0%, rgba(10,0,32,0.45) 50%, transparent 100%)` }} />
                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(${B.pRgb},0.28) 0%, transparent 60%)` }} />
                <div style={{ position: 'absolute', bottom: 12, left: 14, right: 14 }}>
                  <h4 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.1rem', fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1.2 }}>{p.title}</h4>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '16px 18px 20px' }}>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13.5, lineHeight: 1.75, marginBottom: (p.bullets || p.metrics || p.values) ? 14 : 0 }}>{p.body}</p>

                {p.bullets && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {p.bullets.map((b, bi) => (
                      <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: B.gold, flexShrink: 0 }} />
                        <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12.5 }}>{b}</span>
                      </div>
                    ))}
                  </div>
                )}

                {p.metrics && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {p.metrics.map((m, mi) => (
                      <div key={mi} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)' }}>
                        <p style={{ color: '#fff', fontSize: 11.5, fontWeight: 700, marginBottom: 2 }}>{m.label}</p>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10.5, lineHeight: 1.4 }}>{m.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {p.values && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {p.values.map((v, vi) => {
                      const VI = v.icon;
                      return (
                        <div key={vi} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '10px 12px', borderRadius: 10,
                          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                          <div style={{ padding: 5, borderRadius: 7, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }}>
                            <VI style={{ width: 12, height: 12, color: B.gold }} />
                          </div>
                          <div>
                            <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 1 }}>{v.name}</p>
                            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, lineHeight: 1.4 }}>{v.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  </section>
);

export default MissionVisionSection;
